import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ValuePropositionCards = () => {
  const valueProps = [
    {
      id: 1,
      title: "React/React Native Developer",
      subtitle: "Building Modern Applications",
      description: "Crafting scalable, performant web and mobile applications using cutting-edge React technologies. From concept to deployment, I deliver solutions that drive business growth.",
      icon: "Code2",
      gradient: "from-blue-500/20 to-cyan-500/20",
      borderColor: "border-blue-500/30",
      iconColor: "text-blue-400",
      bgColor: "bg-blue-500/5",
      projects: [
        {
          name: "E-commerce Platform",
          tech: "React, Node.js, MongoDB",
          image: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=300&h=200&fit=crop"
        },
        {
          name: "Mobile Banking App",
          tech: "React Native, TypeScript",
          image: "https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=300&h=200&fit=crop"
        },
        {
          name: "SaaS Dashboard",
          tech: "Next.js, Tailwind CSS",
          image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=300&h=200&fit=crop"
        }
      ],
      stats: {
        projects: "50+",
        clients: "5+",
        satisfaction: "90%"
      },
      cta: {
        text: "View Projects",
        link: "/projectspage"
      }
    },
    {
      id: 2,
      title: "Community Mentor",
      subtitle: "Empowering Developer Growth",
      description: "Through IMAC program and personalized mentorship, I've helped 200+ developers transition careers, level up skills, and build confidence in their coding journey.",
      icon: "Users",
      gradient: "from-emerald-500/20 to-teal-500/20",
      borderColor: "border-emerald-500/30",
      iconColor: "text-emerald-400",
      bgColor: "bg-emerald-500/5",
      menteeStories: [
        {
          name: "Dev Amstrong",
          role: "Junior → Senior Developer",
          company: "Coding Made Easy",
          image: "",
          quote: "Adrian\'s mentorship changed my career trajectory completely."
        },
        {
          name: "Joyce ",
          role: "Bootcamp → Full-Stack",
          company: "Student",
          image: "",
          quote: "From zero to hero in 6 months with Adrian\'s guidance."
        },
        {
          name: "Adeline Chioma",
          role: "Career Changer",
          company: "Student",
          image: "",
          quote: "The IMAC program gave me confidence to pursue my dreams."
        }
      ],
      stats: {
        mentees: "200+",
        success: "95%",
        programs: "12"
      },
      cta: {
        text: "Learn More",
        link: "/aboutpage"
      }
    },
    {
      id: 3,
      title: "CodersHub Founder",
      subtitle: "Building Developer Community",
      description: "Founded and leading a thriving community of 15,000+ developers across 45 countries. Creating spaces for learning, collaboration, and career growth.",
      icon: "Globe",
      gradient: "from-purple-500/20 to-pink-500/20",
      borderColor: "border-purple-500/30",
      iconColor: "text-purple-400",
      bgColor: "bg-purple-500/5",
      communityMetrics: [
        {
          label: "Active Members",
          value: "120+",
          icon: "Users",
          growth: "+23%"
        },
        {
          label: "Countries",
          value: "5",
          icon: "Globe",
          growth: "+8"
        },
        {
          label: "Projects Shared",
          value: "2",
          icon: "FolderOpen",
          growth: "+156"
        },
        {
          label: "Success Stories",
          value: "100",
          icon: "Trophy",
          growth: "+0"
        }
      ],
      stats: {
        members: "100+",
        countries: "5",
        events: "12+"
      },
      cta: {
        text: "Join Community",
        link: "https://chat.whatsapp.com/JehVIMhaYuSBq1qtPSpxML"
      }
    }
  ];

  return (
    <section className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <Icon name="Sparkles" size={20} className="text-primary" />
            <span className="text-sm font-brand-medium text-primary tracking-wide uppercase">What I Do</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-brand-headline text-foreground mb-6">
            Triple Impact Through
            <span className="block text-primary text-gradient-gold">Code, Community & Mentorship</span>
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto font-brand-body">
            I believe in creating value at multiple levels - building exceptional software, 
            nurturing developer talent, and fostering communities that drive collective growth.
          </p>
        </div>

        {/* Value Proposition Cards */}
        <div className="grid lg:grid-cols-3 gap-8">
          {valueProps?.map((prop, index) => (
            <div
              key={prop?.id}
              className={`group relative bg-card rounded-2xl p-8 border ${prop?.borderColor} hover:border-primary/50 transition-all duration-500 hover:scale-105 brand-shadow hover:brand-shadow-lg`}
            >
              {/* Background Gradient */}
              <div className={`absolute inset-0 bg-gradient-to-br ${prop?.gradient} rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500`}></div>
              
              {/* Content */}
              <div className="relative z-10">
                {/* Header */}
                <div className="flex items-start justify-between mb-6">
                  <div className={`p-3 rounded-xl ${prop?.bgColor} border ${prop?.borderColor}`}>
                    <Icon name={prop?.icon} size={32} className={prop?.iconColor} />
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-brand-headline text-primary">
                      {prop?.stats?.[Object.keys(prop?.stats)?.[0]]}
                    </div>
                    <div className="text-xs text-text-secondary font-brand-body">
                      {Object.keys(prop?.stats)?.[0]}
                    </div>
                  </div>
                </div>

                {/* Title & Description */}
                <div className="mb-8">
                  <h3 className="text-xl font-brand-subheading text-foreground mb-2">
                    {prop?.title}
                  </h3>
                  <p className="text-sm text-primary font-brand-medium mb-4">
                    {prop?.subtitle}
                  </p>
                  <p className="text-text-secondary font-brand-body leading-relaxed">
                    {prop?.description}
                  </p>
                </div>

                {/* Dynamic Content Based on Type */}
                {prop?.projects && (
                  <div className="mb-8">
                    <h4 className="text-sm font-brand-medium text-foreground mb-4">Recent Projects</h4>
                    <div className="space-y-3">
                      {prop?.projects?.slice(0, 2)?.map((project, idx) => (
                        <div key={idx} className="flex items-center space-x-3 p-3 bg-muted rounded-lg">
                          <div className="w-12 h-12 rounded-lg overflow-hidden">
                            <Image
                              src={project?.image}
                              alt={project?.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-brand-medium text-foreground truncate">
                              {project?.name}
                            </div>
                            <div className="text-xs text-text-secondary">
                              {project?.tech}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {prop?.menteeStories && (
                  <div className="mb-8">
                    <h4 className="text-sm font-brand-medium text-foreground mb-4">Success Stories</h4>
                    <div className="space-y-3">
                      {prop?.menteeStories?.slice(0, 2)?.map((story, idx) => (
                        <div key={idx} className="p-3 bg-muted rounded-lg">
                          <div className="flex items-center space-x-3 mb-2">
                            <div className="w-8 h-8 rounded-full overflow-hidden">
                              <Image
                                src={story?.image}
                                alt={story?.name}
                                className="w-full h-full object-cover"
                              />
                            </div>
                            <div>
                              <div className="text-sm font-brand-medium text-foreground">
                                {story?.name}
                              </div>
                              <div className="text-xs text-text-secondary">
                                {story?.role} at {story?.company}
                              </div>
                            </div>
                          </div>
                          <p className="text-xs text-text-secondary italic">
                            "{story?.quote}"
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {prop?.communityMetrics && (
                  <div className="mb-8">
                    <h4 className="text-sm font-brand-medium text-foreground mb-4">Community Growth</h4>
                    <div className="grid grid-cols-2 gap-3">
                      {prop?.communityMetrics?.slice(0, 4)?.map((metric, idx) => (
                        <div key={idx} className="text-center p-3 bg-muted rounded-lg">
                          <div className="flex items-center justify-center mb-1">
                            <Icon name={metric?.icon} size={16} className="text-primary mr-1" />
                            <span className="text-sm font-brand-headline text-foreground">
                              {metric?.value}
                            </span>
                          </div>
                          <div className="text-xs text-text-secondary">
                            {metric?.label}
                          </div>
                          <div className="text-xs text-success font-brand-medium">
                            {metric?.growth}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Stats Row */}
                <div className="grid grid-cols-3 gap-4 mb-8 pt-6 border-t border-border">
                  {Object.entries(prop?.stats)?.map(([key, value], idx) => (
                    <div key={idx} className="text-center">
                      <div className="text-lg font-brand-headline text-primary">
                        {value}
                      </div>
                      <div className="text-xs text-text-secondary capitalize font-brand-body">
                        {key}
                      </div>
                    </div>
                  ))}
                </div>

                {/* CTA */}
                {prop.id === 3 ? (
                  <a
                    href={prop?.cta?.link}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button
                      variant="outline"
                      fullWidth
                      iconName="ArrowRight"
                      iconPosition="right"
                      iconSize={16}
                      className={`${prop?.borderColor} ${prop?.iconColor} hover:${prop?.bgColor} group-hover:border-primary/50`}
                    >
                      {prop?.cta?.text}
                    </Button>
                  </a>
                ) : (
                  <Link to={prop?.cta?.link}>
                    <Button
                      variant="outline"
                      fullWidth
                      iconName="ArrowRight"
                      iconPosition="right"
                      iconSize={16}
                      className={`${prop?.borderColor} ${prop?.iconColor} hover:${prop?.bgColor} group-hover:border-primary/50`}
                    >
                      {prop?.cta?.text}
                    </Button>
                  </Link>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ValuePropositionCards;